/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The collapsed row's Voorwaarden cell: TWO values, tone by colour (D5, D19) —
 * line 1 the cancel deadline ("Opzeggen vóór 1 okt"), line 2 the term
 * ("Verlengt per jaar") — instead of the three glyphs. Under a review that is
 * not `read` the cell is ONE status line ("Niet gecontroleerd" / "Niet
 * gepubliceerd" / "Niet leesbaar") in its own tone; the two questions are then
 * not answered at all. What is said, and in which tone, is decided by
 * src/lib/termsPresentation.ts (`termsRowCell`); this component words it
 * (`offer_terms.row.*`) and paints it (./toneText.ts).
 *
 * Type, in the row's three layouts (plan §6.1, measured in Phase 0):
 *   below lg   14 px on 18 px lines (13 px up to 375 px wide) — the panel's
 *              values over columns 2–3 of the phone/tablet card
 *   lg … xl    12 px on 16 px lines — the line of five
 *   xl         line 1 12 px bold, line 2 11 px semibold, both `leading-4` —
 *              the ten-column row, where the cell is 8.75 rem (140 px) wide
 * Each line is `truncate`d and carries a `title` with the FULL value and the
 * tone's words — a safety net, not a layout: measured in Chromium with Inter in
 * the 140 px xl cell, every line the module can emit fits (the widest, NL
 * "Opzegdatum ontbreekt" 135 px and "Opzeggen t/m 30 aug" 126.5 px, EN "Cancel
 * before 4 Sept" 122.7 px; termsPresentation.test.ts pins them by name).
 *
 * Accessible name: the cell says "Voorwaarden: " first (sr-only) and each line
 * that is not neutral/muted its judgement ("(gunstig)" / "(let op)"), so colour
 * is never the only signal.
 *
 * Use: `<TermsCell cell={termsRowCell({ dossier, contractDuration, termsReview })} />`.
 *
 * The cell is a flex column that clamps its lines to the width it is given; the
 * row owns the grid area (`.rg-terms`) and passes `className` for any layout
 * rule it needs on this root. SSR-safe: no state, no effect.
 */

import React from 'react';
import { cn } from '../../lib/cn';
import type { TermsRowCell } from '../../lib/termsPresentation';
import { useTermsText } from './termsText';
import { TONE_TEXT } from './toneText';

interface TermsCellProps {
  cell: TermsRowCell;
  /** Layout classes for the cell's root (the row's grid area decides the rest). */
  className?: string;
}

/** `max-w-full` as well as `truncate`: in a flex column that centres its items the line must clamp, not overflow. */
const LINE = 'block min-w-0 max-w-full truncate';
const LINE_ONE = 'text-sm leading-[18px] max-[375px]:text-[13px] font-bold lg:text-xs lg:leading-4';
const LINE_TWO =
  'text-sm leading-[18px] max-[375px]:text-[13px] font-semibold lg:text-xs lg:leading-4 xl:text-[11px]';

export const TermsCell: React.FC<TermsCellProps> = ({ cell, className }) => {
  const { t, text, keyText, toneWord } = useTermsText();
  if (cell.lines.length === 0) return null;

  return (
    <span className={cn('flex min-w-0 max-w-full flex-col justify-center', className)}>
      <span className="sr-only">{`${t('table.col_terms')}: `}</span>
      {cell.lines.map((line, i) => {
        const word = toneWord(line.tone);
        const full = text(line.full);
        return (
          <span
            key={`${line.topic}-${i}`}
            title={full ? (word ? `${full} — ${word}` : full) : undefined}
            className={cn(LINE, i === 0 ? LINE_ONE : LINE_TWO, TONE_TEXT[line.tone])}
          >
            {keyText(line.key, line.vars)}
            {word && <span className="sr-only">{` (${word})`}</span>}
          </span>
        );
      })}
    </span>
  );
};
